import { useEffect, useState } from "react";
import type { Message } from "../types.ts";
import { formatMessageAuthor, messageBubbleStyle, parseMessageParts } from "../utils/messageDisplay.ts";
import { splitMessageBody } from "../utils/messageBody.ts";
import { useUiState } from "../utils/uiState.ts";

/** 最新から何件を既定で展開するか（改善 B-4）。これを超える古いメッセージは畳む。 */
const VISIBLE_TAIL = 2;
/** 畳み始める件数。これ未満なら全件そのまま出す。 */
const COLLAPSE_FROM = 4;

function shortTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function MessageParts({ text, internal }: { text: string; internal: boolean }) {
  return (
    <>
      {parseMessageParts(text).map((part, i) => {
        if (part.kind === "mention") {
          return (
            <span key={i} className={"support-msg__mention" + (internal ? " support-msg__mention--internal" : "")}>
              {part.value}
            </span>
          );
        }
        if (part.kind === "mac") {
          return (
            <a key={i} href={part.href} target="_blank" rel="noopener noreferrer" title="dash でこの機体を開く" className="support-msg__mac">
              {part.value}
            </a>
          );
        }
        if (part.kind === "image") {
          return (
            <a key={i} href={part.value} target="_blank" rel="noopener noreferrer" className="support-msg__image">
              <img src={part.value} alt={part.name} />
            </a>
          );
        }
        if (part.kind === "video") {
          return (
            <div key={i} className="support-msg__video">
              <video src={part.value} controls />
              <div className="support-msg__video-name">{part.name}</div>
            </div>
          );
        }
        if (part.kind === "file") {
          return (
            <a key={i} href={part.value} target="_blank" rel="noopener noreferrer" className="support-msg__file">
              📎 {part.name}
            </a>
          );
        }
        return <span key={i}>{part.value}</span>;
      })}
    </>
  );
}

function MessageItem({
  m,
  foldFooters,
  onToggleFold,
}: {
  m: Message;
  foldFooters: boolean;
  onToggleFold: () => void;
}) {
  const { label, variant, internal } = messageBubbleStyle(m);
  const { visible, folded, foldedLines } = splitMessageBody(m.message);
  const canFold = folded.length > 0;
  const showFolded = canFold && !foldFooters;

  return (
    <article className={`support-msg support-msg--${variant}`}>
      {internal && variant === "internal" && <span className="support-msg__internal-badge">社内</span>}
      <div className="support-msg__head">
        <span className="support-msg__label">{label}</span>
        <span className="support-msg__sep">・</span>
        <span className="support-msg__author">{formatMessageAuthor(m.author)}</span>
        <span className="support-msg__sep">・</span>
        <time className="support-msg__at">{shortTime(m.created_at)}</time>
      </div>
      <div className="support-msg__body">
        <MessageParts text={canFold ? visible : m.message} internal={internal} />
        {showFolded && <MessageParts text={folded} internal={internal} />}
      </div>
      {canFold && (
        <button type="button" className="support-msg__fold-toggle" onClick={onToggleFold}>
          {foldFooters ? `⋯ 引用・署名を表示（${foldedLines}行）` : "引用・署名を隠す"}
        </button>
      )}
    </article>
  );
}

export function MessageThread({
  messages,
  msgEndRef,
}: {
  messages: Message[];
  msgEndRef: React.RefObject<HTMLDivElement | null>;
  /** 互換のため受け取るが、左右振り分けを廃止したため表示には使わない */
  currentAuthorEmail?: string | null;
  currentAuthorRole?: string | null;
}) {
  // 引用・署名の折り畳みは利用者単位（チケットごとではない）。既定は畳む。
  const [foldFooters, setFoldFooters] = useUiState<boolean>("body.foldFooters", true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
  }, [messages.length === 0 ? "empty" : messages[0]?.id]);

  const collapsing = !showAll && messages.length >= COLLAPSE_FROM;
  const hiddenCount = collapsing ? messages.length - VISIBLE_TAIL : 0;
  const shown = collapsing ? messages.slice(-VISIBLE_TAIL) : messages;

  return (
    <div className="support-ticket-detail__messages">
      <div className="support-ticket-detail__messages-inner">
        {messages.length === 0 ? (
          <div className="support-msg__empty">メッセージはまだありません</div>
        ) : (
          <>
            {collapsing && (
              <button type="button" className="support-thread-collapse" onClick={() => setShowAll(true)}>
                ⋯ 過去 {hiddenCount} 件を表示
              </button>
            )}
            {shown.map((m) => (
              <MessageItem
                key={m.id}
                m={m}
                foldFooters={foldFooters}
                onToggleFold={() => setFoldFooters((v) => !v)}
              />
            ))}
          </>
        )}
        <div ref={msgEndRef} />
      </div>
    </div>
  );
}
